// src/features/feedback/slices/feedbackSlice.ts
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import api from '../../../utils/api';
import { STATUS } from '../../../utils/status';
import { FeedbackState, FeedbackCreatePayload, FeedbackUpdatePayload, FeedbackListResponse } from '../../interfaces/feedbackInterface';
import extraReducers from '../../extraReducers/feedbackExtraReducers'

const initialState: FeedbackState = {
  results: null,
  feedbacks: {
    count: undefined,
    results: [],
  },
  error: null,
  createFeedbackStatus: STATUS.NOT_STARTED,
  getFeedbackStatus: STATUS.NOT_STARTED,
  updateFeedbackStatus: STATUS.NOT_STARTED,
  deleteFeedbackStatus: STATUS.NOT_STARTED,
  singleFeedbackStatus: STATUS.NOT_STARTED,
  toggleFeedbackStatus: STATUS.NOT_STARTED,
  createFeedbackMessage: '',
  getMessage: '',
  updateFeedbackMessage: '',
  deleteFeedbackMessage: '',
  toggleFeedbackMessage: '',
  feedbackRow: null,
};

export const createFeedback = createAsyncThunk(
  'feedback/POST',
  async (payload: FeedbackCreatePayload, thunkAPI) => {
    try {
      const response = await api.post('feedback/', payload);
      const { status, data } = response;
      if (status === 201 || status === 200) return data;
      return thunkAPI.rejectWithValue({ response, data });
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ message: error?.response?.data?.message || error.message });
    }
  }
);

export const getFeedbackList = createAsyncThunk<
  FeedbackListResponse,{ sub_project_id: string, page?: number; pageSize?: number }>(
  'feedback/GET_LIST',
  async ({ sub_project_id, page, pageSize }, thunkAPI) => {
    try {
         const queryString = new URLSearchParams({
        ...(sub_project_id && { sub_project_id }),
        ...(page && { page: page.toString() }),
        ...(pageSize && { page_size: pageSize.toString() }),
      }).toString();

      const response = await api.get(`feedback/?${queryString}`);
      const { status, data } = response;
      if (status === 200) return data;
      return thunkAPI.rejectWithValue({ response, data });
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ message: error?.response?.data?.message || error.message });
    }
  }
);

export const updateFeedback = createAsyncThunk(
  'feedback/PATCH',
  async ({ id, editPayload }: { id: string; editPayload: FeedbackUpdatePayload }, thunkAPI) => {
    try {
      const response = await api.patch(`feedback/${id}/`, editPayload);
      const { status, data } = response;
      if (status === 200) return data;
      return thunkAPI.rejectWithValue({ response, data });
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ message: error?.response?.data?.message || error.message });
    }
  }
);

export const deleteFeedback = createAsyncThunk(
  'feedback/DELETE',
  async (id: string, thunkAPI) => {
    try {
      const response = await api.delete(`feedback/${id}/`);
      const { status } = response;
      if (status === 204 || status === 200) return { id };
      return thunkAPI.rejectWithValue({ response });
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ message: error?.response?.data?.message || error.message });
    }
  }
);

export const toggleFeedback = createAsyncThunk(
  'feedback/TOGGLE',
  async ({ id, is_enabled }: { id: string; is_enabled: boolean }, thunkAPI) => {
    try {
      const response = await api.patch(`feedback/${id}/toggle/`, { is_enabled });
      const { status, data } = response;
      if (status === 200) return data;
      return thunkAPI.rejectWithValue({ response, data });
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ message: error?.response?.data?.message || error.message });
    }
  }
);

export const singleFeedback = createAsyncThunk(
  'feedback/GET_SINGLE',
  async (feedbackId: string, thunkAPI) => {
    try {
      const response = await api.get(`feedback/${feedbackId}/`);
      const { status, data } = response;
      if (status === 200) return data;
      return thunkAPI.rejectWithValue({ response, data });
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ message: error?.response?.data?.message || error.message });
    }
  }
);

const feedbackSlice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {
    resetCreateStatus(state) {
      state.createFeedbackStatus = STATUS.NOT_STARTED;
      state.createFeedbackMessage = '';
    },
    resetUpdateStatus(state) {
      state.updateFeedbackStatus = STATUS.NOT_STARTED;
      state.updateFeedbackMessage = '';
    },
    resetDeleteStatus(state) {
      state.deleteFeedbackStatus = STATUS.NOT_STARTED;
      state.deleteFeedbackMessage = '';
    },
    resetToggleStatus(state) {
      state.toggleFeedbackStatus = STATUS.NOT_STARTED;
      state.toggleFeedbackMessage = '';
    },
    optimisticToggle(state, action) {
      const { id, is_enabled } = action.payload;

      if (state.feedbacks?.results) {
        state.feedbacks.results = state.feedbacks.results.map((item: any) =>
          item.id === id ? { ...item, is_enabled } : item
        );
      }
    },
    clearFeedbackRow: (state) => {
      state.feedbackRow = null;
    }
  },
  extraReducers,
});

export const { resetCreateStatus, resetUpdateStatus, resetToggleStatus, resetDeleteStatus, optimisticToggle, clearFeedbackRow } = feedbackSlice.actions;
export default feedbackSlice.reducer;
